import { AbstractDialog } from "./abstract_dialog.js";
import { ConfigSyncService } from "../../config_sync/config_sync_service.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { DialogId } from "./dialog_id.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { resetLinkedSocialProviders } from "./account_providers_preview_state.js";
import { performLogin } from "../../api/perform_login.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { clearFacebookAvatarCache } from "../../common/facebook_avatar_loader.js";
import { FacebookSignInPlugin } from "../../plugins/facebook_sign_in_plugin.js";
import { GoogleSignInPlugin } from "../../plugins/play_games_plugin.js";
import { ImgID } from "../../image_loader/img_id.js";
import { AppleSignInPlugin } from "../../plugins/apple_sign_in_plugin.js";
import { AppleWebSignIn } from "../../plugins/apple_web_sign_in.js";
import { Vector2 } from "../../common/vector2.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { audioController } from "../../audio/audio_controller.js";
import { deleteAccount } from "../../api/requests/auth.requests.js";
import { DeleteAccountNotification } from "./delete_account_notification.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";

export class DeleteAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.DELETE_ACCOUNT_DIALOG;

  private static readonly COUNTDOWN_SECONDS = 5;

  private bg!: RoundedRectangle;
  private content_bg!: RoundedRectangle;
  private cancel_btn!: RoundedRectangle;
  private delete_btn!: RoundedRectangle;
  private is_expanded: boolean = false;
  private confirmation_built: boolean = false;
  private is_confirmed: boolean = false;
  private mark_image!: UIImage;
  private delete_btn_label!: UITextElement;
  private countdown_start: number = -1;
  private countdown_last_shown: number = -1;

  constructor()
  {
    const body_w_design = 990;
    const body_h_design = 776;

    const body_w = LayoutScaler.widthFromDesign( body_w_design );
    const body_h = LayoutScaler.heightFromDesign( body_h_design );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTitle();
    this.createContent();
    this.createActionButtons();
    this.createCloseIcon();
  }

  public name(): string { return "DeleteAccountDialog"; }

  private createBackground(): void
  {
    const bg_color = "#FFFFFF";
    const bg_corner_radius_design = { tl: 30, tr: 30, br: 70, bl: 70 };

    const circle_w_design = 237;
    const circle_h_design = 234;
    const circle_radius_design = 117;
    const circle_y_design = 136;

    const icon_w_design = 160;
    const icon_h_design = 140;
    const icon_y_design = 9;

    const circle_w = LayoutScaler.widthFromDesign( circle_w_design );
    const circle_h = LayoutScaler.heightFromDesign( circle_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( circle_radius_design );
    const circle_y = LayoutScaler.yFromDesign( circle_y_design );

    const icon_w = LayoutScaler.widthFromDesign( icon_w_design );
    const icon_h = LayoutScaler.heightFromDesign( icon_h_design );
    const icon_y = LayoutScaler.yFromDesign( icon_y_design );

    this.bg = new RoundedRectangle(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tl ),
        tr: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tr ),
        br: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.br ),
        bl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.bl )
      },
      bg_color
    );
    this.addChild( this.bg );

    const circle_x = this.rect.getW2() - circle_w / 2;

    const circle = new RoundedRectangle(
      this.ctx,
      circle_x,
      -circle_y,
      circle_w,
      circle_h,
      r,
      bg_color
    );
    this.addChild( circle );

    const icon_x = ( circle_w - icon_w ) / 2;

    const warning = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.IconWarningYellow
    );
    circle.addChild( warning, true );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h_design = 68;
    const title_h = LayoutScaler.heightFromDesign( title_h_design );
    const title_x = ( this.rect.getW() - title_w ) / 2;
    const title_y_design = 49;
    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_font_size_design = 50;

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "PROGRESS_SAVED_DIALOG.ACCOUNT_DELETE",
        text_color: ColorScheme.red,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( title_font_size_design ),
        font_weight: 700,
        auto_wrap: false,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.addChild( title );
  }

  private createContent(): void
  {
    const content_w_design = 948;
    const content_h_design = 469;
    const content_w = LayoutScaler.widthFromDesign( content_w_design );
    const content_h = LayoutScaler.heightFromDesign( content_h_design );
    const content_x = this.rect.getW2() - content_w / 2;
    const content_y_design = 134;
    const content_y = LayoutScaler.yFromDesign( content_y_design );

    this.content_bg = new RoundedRectangle(
      this.ctx,
      content_x,
      content_y,
      content_w,
      content_h,
      LayoutScaler.cornerRadiusFromDesign( 0 ),
      "#FFF3E8"
    );
    this.bg.addChild( this.content_bg );

    const text_w_design = 906;
    const text_h_design = 424;
    const text_w = LayoutScaler.widthFromDesign( text_w_design );
    const text_h = LayoutScaler.heightFromDesign( text_h_design );
    const text_x = ( content_w - text_w ) / 2;
    const text_y = ( content_h - text_h ) / 2;
    const text_font_size_design = 40;

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.TEXT",
        text_color: "#A85F1E",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( text_font_size_design ),
        font_weight: 700,
        auto_wrap: true,
        using_y_offset: false,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.content_bg.addChild( text );
  }

  private createActionButtons(): void
  {
    const btn_w_design = 462;
    const btn_h_design = 112;
    const btn_radius_design = 56;
    const gap_design = 23;
    const btn_y_design = 637;
    const btn_font_size_design = 48;

    const btn_w = LayoutScaler.widthFromDesign( btn_w_design );
    const btn_h = LayoutScaler.heightFromDesign( btn_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( btn_radius_design );
    const gap = LayoutScaler.widthFromDesign( gap_design );
    const btn_y = LayoutScaler.yFromDesign( btn_y_design );

    const row_w = btn_w * 2 + gap;
    const row_x = this.rect.getW2() - row_w / 2;

    this.cancel_btn = new RoundedRectangle(
      this.ctx,
      row_x,
      btn_y,
      btn_w,
      btn_h,
      r,
      "#12B2E2",
      undefined,
      true
    );
    this.cancel_btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
    } );
    this.cancel_btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CLEAR_STATISTICS.NO",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( btn_font_size_design ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_light_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    ), true );
    this.addChild( this.cancel_btn );

    this.delete_btn = new RoundedRectangle(
      this.ctx,
      row_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      r,
      "#F28519",
      undefined,
      true
    );
    this.delete_btn.setOnClick( () =>
    {
      audioController.playSound( AudioClips.UI_CLICK );
      this.expandDialog();
    } );
    this.delete_btn_label = new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.BUTTON_TEXT",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( btn_font_size_design ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_orange_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    this.delete_btn.addChild( this.delete_btn_label, true );
    this.addChild( this.delete_btn );
  }

  private createCloseIcon(): void
  {
    const icon_size_design = 85;
    const icon_size = LayoutScaler.widthFromDesign( icon_size_design );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 7 );
    const icon_y = LayoutScaler.yFromDesign( 7 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () => { DialogManager.closeDialog(); } );
    this.addChild( icon );
  }

  private createConfirmation(): void
  {
    if ( this.confirmation_built ) return;
    this.confirmation_built = true;

    const sub_w_design = 70;
    const sub_text_h_design = 70;
    const sub_x_design = 296;
    const sub_text_y_design = 636;
    const sub_w = LayoutScaler.widthFromDesign( sub_w_design );
    const sub_text_h = LayoutScaler.heightFromDesign( sub_text_h_design );
    const sub_x = LayoutScaler.xFromDesign( sub_x_design );
    const sub_text_y = LayoutScaler.yFromDesign( sub_text_y_design );

    const substrate = new UIImage(
      this.ctx,
      sub_x,
      sub_text_y,
      sub_w,
      sub_text_h,
      ImgID.UI_ConfirmSubstrate,
      {},
      true
    );
    substrate.setOnClick( () => { this.onSubstrateClick(); } );
    this.addChild( substrate );

    const text_w_design = 350;
    const text_x_design = 399;
    const text_y_design = 640;
    const text_w = LayoutScaler.widthFromDesign( text_w_design );
    const text_x = LayoutScaler.xFromDesign( text_x_design );
    const text_y = LayoutScaler.yFromDesign( text_y_design );
    const confirm_text_font_size_design = 47;

    const confirm_text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      sub_text_h,
      {
        localization_key: "DELETE_ACCOUNT_DIALOG.CONFIRM_TEXT",
        text_color: "#A85F1E",
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( confirm_text_font_size_design ),
        font_weight: 700,
      }
    );
    this.addChild( confirm_text, true );

    const mark_w_design = 102;
    const mark_h_design = 102;
    const mark_x_design = 278;
    const mark_y_design = 613;
    const mark_w = LayoutScaler.widthFromDesign( mark_w_design );
    const mark_h = LayoutScaler.heightFromDesign( mark_h_design );
    const mark_x = LayoutScaler.xFromDesign( mark_x_design );
    const mark_y = LayoutScaler.yFromDesign( mark_y_design );

    this.mark_image = new UIImage(
      this.ctx,
      mark_x,
      mark_y,
      mark_w,
      mark_h,
      ImgID.UI_SettingsDialogCloseBtn,
      { image_opacity: 0 }
    );
    this.addChild( this.mark_image, true );
  }

  private onSubstrateClick(): void
  {
    this.is_confirmed = !this.is_confirmed;
    audioController.playSound( AudioClips.UI_CLICK );
    ( this.mark_image as any ).image_opacity = this.is_confirmed ? 1 : 0;
    this.forceRedraw();
    this.updateDeleteBtnState();
  }

  private updateDeleteBtnState(): void
  {
    if ( this.countdown_last_shown !== 0 ) return;

    if ( !this.is_confirmed )
    {
      this.delete_btn.setColor( "#808080" );
      setButtonTextOutline( [ this.delete_btn_label ], false, ColorScheme.red_btn_outline );
      this.delete_btn.setOnClick( () => {} );
    }
    else
    {
      this.delete_btn.setColor( ColorScheme.red );
      setButtonTextOutline( [ this.delete_btn_label ], true, ColorScheme.red_btn_outline );
      this.delete_btn.setOnClick( () =>
      {
        audioController.playSound( AudioClips.UI_CLICK );
        void this.performDeleteAccount();
      } );
    }

    this.delete_btn.forceRedraw();
    this.forceRedraw();
  }

  private async performDeleteAccount(): Promise<void>
  {
    // const session = AuthSessionStore.get();
    // if ( !session ) return;

    await deleteAccount();

    const isNative = ( window as any ).Capacitor?.getPlatform?.() === 'ios' ||
                     ( window as any ).Capacitor?.getPlatform?.() === 'android';

    await Promise.allSettled( [
      GoogleSignInPlugin.signOut(),
      FacebookSignInPlugin.signOut(),
      ( isNative ? AppleSignInPlugin : AppleWebSignIn ).signOut(),
    ] );

    localStorage.removeItem( 'web_google_id' );
    localStorage.removeItem( 'web_facebook_id' );
    localStorage.removeItem( 'native_apple_id' );
    localStorage.removeItem( 'analytics_userid' );
    localStorage.removeItem( 'player_nickname' );
    localStorage.removeItem( 'player_avatar_id' );
    localStorage.removeItem( 'player_facebook_avatar_url' );
    clearFacebookAvatarCache();
    StatisticsStorage.remove();

    // AuthSessionStore.set( null as any );
    CumulativeBoosterService.reset();
    resetLinkedSocialProviders();

    DialogManager.closeAllDialogs();
    DialogManager.openDialog( new DeleteAccountNotification( async () =>
    {
      await performLogin();
      await ConfigSyncService.refreshAll( 'account_switch' );
      ScreenManager.showScreen( Screens.MENU );
    } ) );
  }

  private expandDialog(): void
  {
    if ( this.is_expanded ) return;
    this.is_expanded = true;
    const body_h_expanded_design = 888;
    const btn_y_expanded_design = 755;

    const newBodyH = LayoutScaler.heightFromDesign( body_h_expanded_design );

    const currentPos = this.pixelPosImportant();
    const newBodyY = ( DialogManager.getHeight() - newBodyH ) / 2;
    this.setPixelPos( new Vector2( currentPos.x, newBodyY ), true );

    this.rect.resize( this.rect.getW(), newBodyH );
    this.bg.getRect().resize( this.bg.getRect().getW(), newBodyH );

    const btn_y = LayoutScaler.yFromDesign( btn_y_expanded_design );

    const cancelPos = this.cancel_btn.pixelPosImportant();
    this.cancel_btn.setPixelPos( new Vector2( cancelPos.x, btn_y ), true );

    const deletePos = this.delete_btn.pixelPosImportant();
    this.delete_btn.setPixelPos( new Vector2( deletePos.x, btn_y ), true );

    this.delete_btn.setColor( "#808080" );
    setButtonTextOutline( [ this.delete_btn_label ], false, ColorScheme.red_btn_outline );
    this.delete_btn.setOnClick( () => {} );
    this.countdown_start = performance.now();

    this.createConfirmation();
    this.forceRedraw();
  }

  public override update( now: number ): void
  {
    if ( this.countdown_start >= 0 )
    {
      const elapsed   = now - this.countdown_start;
      const remaining = Math.ceil( ( DeleteAccountDialog.COUNTDOWN_SECONDS * 1000 - elapsed ) / 1000 );
      const display   = Math.max( 0, remaining );

      if ( display !== this.countdown_last_shown )
      {
        this.countdown_last_shown = display;
        this.delete_btn.forceRedraw();
        this.forceRedraw();

        if ( display === 0 )
        {
          this.countdown_start = -1;
          this.delete_btn_label.setLocalizationKey( "DELETE_ACCOUNT_DIALOG.BUTTON_TEXT" );
          this.updateDeleteBtnState();
        }
        else
        {
          this.delete_btn_label.setText( String( display ) );
        }
      }
    }

    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
